import { AdminLayout } from "./AdminLayout";
import { Plus, Search, MoreVertical, PoundSterling, FolderPlus } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Deals = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    title: '', value: '', currency: 'GBP', stage: 'new', company_id: '', notes: ''
  });

  const fetchData = async () => {
    try {
      const params = stageFilter ? { stage: stageFilter } : {};
      const [dealsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/crm/deals`, { params, withCredentials: true }),
        axios.get(`${API}/crm/companies`, { withCredentials: true })
      ]);
      setDeals(dealsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [stageFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, value: parseFloat(formData.value) || 0 };
      if (editingDeal) {
        await axios.put(`${API}/crm/deals/${editingDeal.deal_id}`, data, { withCredentials: true });
        toast.success("Deal updated");
      } else {
        await axios.post(`${API}/crm/deals`, data, { withCredentials: true });
        toast.success("Deal created");
      }
      setIsDialogOpen(false);
      setEditingDeal(null);
      setFormData({ title: '', value: '', currency: 'GBP', stage: 'new', company_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error("Failed to save deal");
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title, value: deal.value.toString(), currency: deal.currency,
      stage: deal.stage, company_id: deal.company_id || '', notes: deal.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await axios.delete(`${API}/crm/deals/${dealId}`, { withCredentials: true });
      toast.success("Deal deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete deal");
    }
  };

  const updateStage = async (dealId, stage) => {
    try {
      const response = await axios.put(`${API}/crm/deals/${dealId}`, { stage }, { withCredentials: true });
      
      // Check if a project was auto-created (when moving to won)
      if (response.data.project_created) {
        toast.success(
          <div className="flex items-center gap-2">
            <FolderPlus className="w-4 h-4" />
            <span>Deal closed. Delivery created.</span>
          </div>,
          {
            action: {
              label: 'View',
              onClick: () => navigate('/admin/projects')
            }
          }
        );
      } else {
        toast.success("Stage updated");
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const getCompanyName = (companyId) => companies.find(c => c.company_id === companyId)?.name || '';

  const stages = ['new', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  const stageLabels = { new: 'New', qualified: 'Qualified', proposal: 'Proposal', negotiation: 'Negotiation', won: 'Won', lost: 'Lost' };
  const stageColors = { new: 'bg-blue-500/20 text-blue-400', qualified: 'bg-cyan-500/20 text-cyan-400', proposal: 'bg-yellow-500/20 text-yellow-400', negotiation: 'bg-purple-500/20 text-purple-400', won: 'bg-green-500/20 text-green-400', lost: 'bg-red-500/20 text-red-400' };

  // Group deals by stage for Kanban view
  const dealsByStage = stages.reduce((acc, stage) => {
    acc[stage] = deals.filter(d => d.stage === stage);
    return acc;
  }, {});

  return (
    <AdminLayout title="Pipeline">
      <div className="space-y-6" data-testid="admin-deals">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Deal Pipeline</h2>
            <p className="text-sm text-[#8A8A8A]">
              Total value: £{deals.reduce((sum, d) => sum + (d.value || 0), 0).toLocaleString()}
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm flex items-center gap-2"
                onClick={() => { setEditingDeal(null); setFormData({ title: '', value: '', currency: 'GBP', stage: 'new', company_id: '', notes: '' }); }}
                data-testid="add-deal-btn">
                <Plus className="w-4 h-4" /> Add Deal
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E]">
              <DialogHeader><DialogTitle>{editingDeal ? 'Edit Deal' : 'Add New Deal'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <input type="text" placeholder="Deal Title *" required value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-field" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                    <input type="number" placeholder="Value" value={formData.value} min="0" step="0.01"
                      onChange={(e) => setFormData({...formData, value: e.target.value})} className="input-field pl-10" />
                  </div>
                  <select value={formData.stage} onChange={(e) => setFormData({...formData, stage: e.target.value})} className="input-field">
                    {stages.map(stage => <option key={stage} value={stage}>{stageLabels[stage]}</option>)}
                  </select>
                </div>
                <select value={formData.company_id} onChange={(e) => setFormData({...formData, company_id: e.target.value})} className="input-field">
                  <option value="">Select Company</option>
                  {companies.map(company => <option key={company.company_id} value={company.company_id}>{company.name}</option>)}
                </select>
                <textarea placeholder="Notes" rows={3} value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})} className="input-field resize-none" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary text-sm">{editingDeal ? 'Update' : 'Create'} Deal</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => (
              <div key={stage} className="w-72 flex-shrink-0">
                <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <span className={`badge ${stageColors[stage]}`}>{stageLabels[stage]}</span>
                    <span className="text-sm text-[#8A8A8A]">{dealsByStage[stage].length}</span>
                  </div>
                  <div className="text-sm text-[#8A8A8A] mt-1">
                    £{dealsByStage[stage].reduce((sum, d) => sum + (d.value || 0), 0).toLocaleString()}
                  </div>
                </div>
                <div className="space-y-3">
                  {loading ? (
                    [...Array(2)].map((_, i) => (
                      <div key={i} className="card-dashboard animate-pulse">
                        <div className="h-4 bg-[#2E2E2E] rounded w-3/4 mb-2" />
                        <div className="h-3 bg-[#2E2E2E] rounded w-1/2" />
                      </div>
                    ))
                  ) : dealsByStage[stage].length === 0 ? (
                    <div className="text-center py-8 text-[#8A8A8A] text-sm border border-dashed border-[#2E2E2E]">
                      No deals
                    </div>
                  ) : (
                    dealsByStage[stage].map((deal) => (
                      <div key={deal.deal_id} className="card-dashboard" data-testid={`deal-card-${deal.deal_id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-sm">{deal.title}</h4>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 hover:bg-[#2E2E2E] -mr-2 -mt-1"><MoreVertical className="w-4 h-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                              <DropdownMenuItem onClick={() => handleEdit(deal)} className="cursor-pointer hover:bg-[#2E2E2E]">Edit</DropdownMenuItem>
                              {stages.filter(s => s !== stage).map(s => (
                                <DropdownMenuItem key={s} onClick={() => updateStage(deal.deal_id, s)} className="cursor-pointer hover:bg-[#2E2E2E]">
                                  Move to {stageLabels[s]}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuItem onClick={() => handleDelete(deal.deal_id)} className="cursor-pointer hover:bg-[#2E2E2E] text-red-400">Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="text-lg font-bold text-[#5F8FB8]">£{(deal.value || 0).toLocaleString()}</div>
                        {deal.company_id && (
                          <div className="text-xs text-[#8A8A8A] mt-2">{getCompanyName(deal.company_id)}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Deals;
